// What the editor checks a line for — the «Проверка текста» switches of the
// visibility popover, as the detectors read them (research:
// docs/research/editor-error-underline.md §9.1, §9.8 risk 9).
//
// Screenplays are full of slang, names and misspellings on purpose, mostly in
// the dialogue: a check that cannot be told to keep quiet there kills the
// feature. So each checked block type has its own level, «Реплика» is checked
// for spelling only until the writer says otherwise, and a category a level
// leaves out is never run — not run and then left unpainted.

import { FountainBlockType } from '@/types/fountain';
import type { IssueCode } from './lineIssues';

/** The kinds of problem the research sorts screenplay issues into (§9.1). */
export type IssueCategory = 'structure' | 'consistency' | 'spelling' | 'grammar' | 'typography';

/** Every category — what a caller that names no levels is checked for. */
export const ALL_ISSUE_CATEGORIES: ReadonlySet<IssueCategory> = new Set(['structure', 'consistency', 'spelling', 'grammar', 'typography']);
const SPELLING_ONLY: ReadonlySet<IssueCategory> = new Set(['spelling']);
const NONE: ReadonlySet<IssueCategory> = new Set();

const CATEGORY_OF: Readonly<Record<IssueCode, IssueCategory>> = {
    'remark-unclosed': 'structure',
    'remark-empty': 'structure',
    'heading-no-interior': 'structure',
    'heading-no-time': 'structure',
    'cue-unknown': 'consistency',
    'heading-location-unknown': 'consistency',
    'heading-sublocation-unknown': 'consistency',
    'double-space': 'typography',
    'hyphen-dash': 'typography',
    'straight-quotes': 'typography',
    misspelled: 'spelling',
    grammar: 'grammar',
};

export function issueCategoryOf(code: IssueCode): IssueCategory {
    return CATEGORY_OF[code];
}

/** How much of a block type is checked: nothing, its spelling, or everything. */
export type IssueCheckLevel = 'off' | 'spelling' | 'all';

/** One switch per checked block type, in the order the popover lists them. A
 *  cue and a heading are names and conventions, not spelled prose (a cue is
 *  never looked up in a dictionary, a heading's words live in inputs), so they
 *  are only on or off. */
export const ISSUE_CHECK_BLOCKS = [
    { blockType: FountainBlockType.SCENE_HEADING, levels: ['off', 'all'], defaultLevel: 'all' },
    { blockType: FountainBlockType.LOGLINE, levels: ['off', 'spelling', 'all'], defaultLevel: 'all' },
    { blockType: FountainBlockType.PLOT, levels: ['off', 'spelling', 'all'], defaultLevel: 'all' },
    { blockType: FountainBlockType.ACTION, levels: ['off', 'spelling', 'all'], defaultLevel: 'all' },
    { blockType: FountainBlockType.CHARACTER, levels: ['off', 'all'], defaultLevel: 'all' },
    { blockType: FountainBlockType.DIALOGUE, levels: ['off', 'spelling', 'all'], defaultLevel: 'spelling' },
    { blockType: FountainBlockType.PARENTHETICAL, levels: ['off', 'spelling', 'all'], defaultLevel: 'all' },
    // A note is the writers talking to each other, not the script: spelled,
    // and nothing else (`useSpellFields` reads its card's fields).
    { blockType: FountainBlockType.NOTE, levels: ['off', 'spelling'], defaultLevel: 'spelling' },
] as const satisfies ReadonlyArray<{ blockType: FountainBlockType; levels: readonly IssueCheckLevel[]; defaultLevel: IssueCheckLevel }>;

export type IssueCheckBlockType = (typeof ISSUE_CHECK_BLOCKS)[number]['blockType'];

export type IssueCheckLevels = Readonly<Record<IssueCheckBlockType, IssueCheckLevel>>;

export const DEFAULT_ISSUE_CHECK_LEVELS: IssueCheckLevels = Object.fromEntries(
    ISSUE_CHECK_BLOCKS.map(({ blockType, defaultLevel }) => [blockType, defaultLevel]),
) as Record<IssueCheckBlockType, IssueCheckLevel>;

const LEVEL_CATEGORIES: Readonly<Record<IssueCheckLevel, ReadonlySet<IssueCategory>>> = {
    off: NONE,
    spelling: SPELLING_ONLY,
    all: ALL_ISSUE_CATEGORIES,
};

/** The categories a line of `blockType` is checked for. A block type with no
 *  switch (a transition…) has no detector either. */
export function categoriesFor(levels: IssueCheckLevels, blockType: FountainBlockType): ReadonlySet<IssueCategory> {
    const checked = ISSUE_CHECK_BLOCKS.find(b => b.blockType === blockType);
    return LEVEL_CATEGORIES[checked ? levels[checked.blockType] : 'off'];
}

/**
 * The stored levels, read back: each block's level when it is one the block
 * offers, its default otherwise — so a value saved before a block got its
 * switch, or edited by hand, still reads as a whole set of levels.
 */
export function normalizeIssueCheckLevels(raw: unknown): IssueCheckLevels {
    const stored = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
    const out: Record<IssueCheckBlockType, IssueCheckLevel> = { ...DEFAULT_ISSUE_CHECK_LEVELS };
    for (const { blockType, levels } of ISSUE_CHECK_BLOCKS) {
        const offered: readonly IssueCheckLevel[] = levels;
        const level = offered.find(l => l === stored[blockType]);
        if (level !== undefined) out[blockType] = level;
    }
    return out;
}
